/**
 * preview-selection-toggle.tsx — Phase 048 Plan 02 Task 2 (D-048-01 / D-048-02).
 *
 * A two-state segmented button group that drives the `previewMode` flag on
 * the `preview-selection-slice`. Shape and class palette mirror the
 * Web/Mobile device toggle in `preview-surface.tsx` (L53-79) — a paired
 * `<button>` group with `aria-current` + the active/inactive class swap —
 * so the two toggles read as a single chrome family in the header bar.
 *
 * The toggle DRIVES the slice (`setPreviewMode`) directly; it owns NO local
 * state. The `usePreviewSelection` listener + `PreviewSelectionOverlay` —
 * siblings of this component, not children — read the slice imperatively
 * (and reactively in the overlay), so the toggle and the layer stay
 * decoupled. RESEARCH "Don't Hand-Roll" → "a slice, not lifted useState".
 *
 * Mount contract: rendered ONLY on the ABox deeplink path (Task 3 gates the
 * render on `currentProject?.aboxElementMap != null` inside `preview-surface.tsx`);
 * the toggle never appears on template / `openServerProject` / e2e Preview
 * paths — D-048-03.
 *
 * Icons: `lucide-react` `Hand` (Interact) / `MousePointer2` (Select) — both
 * already part of the dependency surface (`preview-surface.tsx` imports
 * `ExternalLink` from the same package).
 */

import { Hand, MousePointer2 } from 'lucide-react'
import { usePreviewSelectionStore } from '@/features/preview/store/preview-selection-slice'

const ACTIVE_CLS =
  'px-2.5 py-1 text-[11px] font-medium rounded bg-card text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)] inline-flex items-center gap-1'
const INACTIVE_CLS =
  'px-2.5 py-1 text-[11px] font-medium rounded bg-transparent text-muted-foreground hover:text-foreground inline-flex items-center gap-1'

export function PreviewSelectionToggle() {
  const previewMode = usePreviewSelectionStore((s) => s.previewMode)
  const setPreviewMode = usePreviewSelectionStore((s) => s.setPreviewMode)

  return (
    <div
      className="flex items-center gap-1"
      data-testid="preview-selection-toggle"
    >
      <button
        type="button"
        aria-label="Interact"
        aria-current={previewMode === 'interact' ? 'page' : undefined}
        onClick={() => setPreviewMode('interact')}
        className={previewMode === 'interact' ? ACTIVE_CLS : INACTIVE_CLS}
      >
        <Hand size={12} strokeWidth={1.7} />
        Interact
      </button>
      <button
        type="button"
        aria-label="Select"
        aria-current={previewMode === 'select' ? 'page' : undefined}
        onClick={() => setPreviewMode('select')}
        className={previewMode === 'select' ? ACTIVE_CLS : INACTIVE_CLS}
      >
        <MousePointer2 size={12} strokeWidth={1.7} />
        Select
      </button>
    </div>
  )
}
